import { StatusCodes } from 'http-status-codes';
import { AxiosResponse } from 'axios';

import ApiService from 'services/ApiService/ApiService';
import ConfigurationService, { ConfigurationKeys } from 'services/ConfigurationService';
import { translate } from 'services/i18nService';
import getMessagingServiceInstance, { MessagingService } from 'services/MessagingService/MessagingService';
import * as eventNames from 'services/MessagingService/EventNames';
import NotificationService from 'services/NotificationService';
import SessionStorageService from 'services/SessionStorageService.js';
import TracingService from 'services/TracingService';
import userStore from 'stores/UserStore';
import { IMPERSONATE_KEY } from 'enum/profile';
import parseCatch from 'utils/parseCatch';
import LocalStorageService from 'services/LocalStorageService';
import ACCESS_LEVEL_NUM from 'enum/accessLevelsNum';
import { User } from 'stores/UserStore/UserStore.types';
import { REQUEST_ERROR_STATUS } from 'enum/accountManagementFlowsV2';

import ModalService from './ModalService';


const BASE_URL = ConfigurationService.get(ConfigurationKeys.BASE_URL);
const LOGIN_INFO_URL = `${BASE_URL}/rest/v2.0/login/user`;
const LOGIN_URL = `${BASE_URL}/rest/v2.0/login`;
const TWO_FA_LOGIN_URL = `${BASE_URL}/rest/v2.0/login/twoFa`;
const LOGOUT_URL = `${BASE_URL}/rest/v2.0/login/logout`;
const PASSWORD_RESET_EMAIL_URL = `${BASE_URL}/rest/v1.0/passwords`;
const PASSWORD_CHANGE_URL = `${BASE_URL}/rest/v1.0/passwords/{username}`;
const USER_MODALS_URL = `${BASE_URL}/rest/v1.0/users/{username}/modals`;
const UI_VARIABLES_URL = `${BASE_URL}/rest/v1.0/ui-variables`;
const TWO_FA_URL = `${BASE_URL}/rest/v1.0/users/{username}/sms-pending-operations/{operationId}`;

export const RESEND_EMAIL_URL = `${BASE_URL}/rest/v1.0/verification-emails`;

class UserService {

  constructor(private messagingService: MessagingService) {}

  /**
   * @method changePassword - Changing password
   * {@link https://cam4com.atlassian.net/wiki/spaces/RBN/pages/2070609977/Password+APIs#Changing-Password }
   * @emits PASSWORD_CHANGE_RESPONSE when API call is successful
   * @emits PASSWORD_CHANGE_ERROR_400 when API call has 400 error with data and reason why it's failing
   * @emits PASSWORD_CHANGE_ERROR_403 when API call has 403 error with data and reason why it's failing
   * @emits PASSWORD_CHANGE_ERROR when API call has 404 or 500 error
   */
  changePassword = (params: {
    currentPassword: string;
    newPassword: string;
    username: User['username'];
  }): void => {
    const { currentPassword, newPassword, username } = params;

    const url = ApiService.substitute(PASSWORD_CHANGE_URL, { username });
    const data = { currentPassword, newPassword };

    ApiService.patch(encodeURI(url), { data })
      .then((response) => {
        this.messagingService.emit(eventNames.PASSWORD_CHANGE_RESPONSE, response.data);
        NotificationService.create({
          type: NotificationService.type.TOAST,
          subtype: NotificationService.subtype.SUCCESS,
          text: translate('account.security.managePassword.changed'),
          autoClose: 1500,
        });
      })
      .catch((error) => {
        switch (error.response.status) {
          case StatusCodes.BAD_REQUEST: {
            this.messagingService.emit(eventNames.PASSWORD_CHANGE_ERROR_400, error.response.data);
            break;
          }
          case StatusCodes.FORBIDDEN: {
            this.messagingService.emit(eventNames.PASSWORD_CHANGE_ERROR_403, error.response.data);
            break;
          }
          default: {
            this.messagingService.emit(eventNames.PASSWORD_CHANGE_ERROR);
          }
        }
      });
  };

  /**
   * @method resetPassword - Resetting password
   * @{@link https://cam4com.atlassian.net/wiki/spaces/RBN/pages/2070609977/Password+APIs#Resetting-Password }
   * @param {string} params.otp - otp taken from the email confirmation link query params, it expires in 15 minutes
   * @emits PASSWORD_RESET_RESPONSE when API call is successful
   * @emits PASSWORD_RESET_ERROR when API call has 404 or 500 error
   * @emits PASSWORD_RESET_400 when API call has 400 error with data and reason why it's failing
   * @emits PASSWORD_RESET_403 when API call has 403 error with data and reason why it's failing
   */
  resetPassword = (params: {
    username: User['username'];
    password: string;
    otp: string;
  }): void => {
    const { username, password, otp } = params;
    const url = ApiService.substitute(PASSWORD_CHANGE_URL, { username });
    const data = { password, otp };

    ApiService.put(encodeURI(url), { data })
      .then((response) => {
        this.messagingService.emit(eventNames.PASSWORD_RESET_RESPONSE, response.data);
      })
      .catch((error) => {
        switch (error.response.status) {
          case StatusCodes.BAD_REQUEST: {
            this.messagingService.emit(eventNames.PASSWORD_RESET_ERROR_400, error.response.data);
            break;
          }
          case StatusCodes.FORBIDDEN: {
            this.messagingService.emit(eventNames.PASSWORD_RESET_ERROR_403, error.response.data);
            break;
          }
          default: {
            this.messagingService.emit(eventNames.PASSWORD_RESET_ERROR);
          }
        }
      });
  };

  /**
   * @method passwordResetEmail - Sending password reset email
   * {@link https://cam4com.atlassian.net/wiki/spaces/RBN/pages/2070609977/Password+APIs#Sending-password-reset-email }
   * @emits PASSWORD_RESET_EMAIL_RESPONSE when API call is successful
   * @emits PASSWORD_RESET_EMAIL_ERROR when API call has error
   */
  passwordResetEmail = (email: string): void => {
    const data = { email };
    ApiService.post(PASSWORD_RESET_EMAIL_URL, { data })
      .then((response) => {
        this.messagingService.emit(eventNames.PASSWORD_RESET_EMAIL_RESPONSE, response.data);
      })
      .catch((error) => {
        switch (error.response.status) {
          case StatusCodes.BAD_REQUEST: {
            this.messagingService.emit(eventNames.PASSWORD_RESET_EMAIL_ERROR_400);
            break;
          }
          case StatusCodes.FORBIDDEN: {
            this.messagingService.emit(eventNames.PASSWORD_RESET_EMAIL_ERROR_403, error.response.data);
            break;
          }
          case StatusCodes.TOO_MANY_REQUESTS: {
            this.messagingService.emit(eventNames.PASSWORD_RESET_EMAIL_ERROR_429, error.response.data);
            break;
          }
          default: {
            this.messagingService.emit(eventNames.PASSWORD_RESET_EMAIL_ERROR);
          }
        }
      });
  };

  /* eslint-disable max-len */
  /**
   * @method resendEmail - Resending the verification email
   * {@link https://cam4com.atlassian.net/wiki/spaces/RBN/pages/1293680979/User+Creation+and+Registration+Api#Resending-the-verification-email }
   * @emits SET_RESEND_EMAIL_RESPONSE when API call is successful
   * @emits RESEND_EMAIL_RESPONSE_ERROR when API call has error
   */
  /* eslint-enable max-len */
  resendEmail = (email: string): void => {
    const data = { email };
    ApiService.post(RESEND_EMAIL_URL, { data })
      .then(() => {
        this.messagingService.emit(eventNames.SET_RESEND_EMAIL_RESPONSE, true);
      })
      .catch(() => {
        this.messagingService.emit(eventNames.RESEND_EMAIL_RESPONSE_ERROR);
        NotificationService.create({
          type: NotificationService.type.TOAST,
          text: translate('account.errorGeneric'),
          subtype: NotificationService.subtype.ERROR,
        });
      });
  };

  /**
   * @method getUserData - Retreiving user info
   * {@link https://cam4com.atlassian.net/wiki/spaces/RBN/pages/1705607215/Login+V2+Api#Retrieving-user-info }
   * @emits GET_USER_INFO_RESPONSE when API call is successful
   * @emits GET_USER_INFO_ERROR when API call has error
   */
  getUserInfo(): void {
    const url = encodeURI(LOGIN_INFO_URL);

    ApiService.get(url)
      .then((response) => {
        this.messagingService.emit(eventNames.GET_USER_INFO_RESPONSE, response.data);
      })
      .catch((error) => {
        this.messagingService.emit(eventNames.GET_USER_INFO_ERROR);
        parseCatch(error);
      });
  }

  /**
   * @method login - Regular login
   * {@link https://cam4com.atlassian.net/wiki/spaces/RBN/pages/1705607215/Login+V2+Api#Regular-Login }
   * @param data.username - it should contain only lower and uppercase letters, digits and _ or -
   * @param data.password - user password
   * @param data.captcha - send the Google captcha when captcha is required
   * @param data.captchaV3 - send the Google captcha when captcha V3 is required
   * @param data.deviceId - send by the mobile app
   * @param data.mbaVersion - sent by the mobile app
   * @emits LOGIN_SUCCESS when API call is successful
   * @emits LOGIN_ERROR when API call has error
   * @emits LOGIN_ERROR_403 when API call has error with information for next action
   */
  login = (data: {
    username: User['username'];
    password: string;
    captcha: string;
    captchaV3: string;
    deviceId: string;
    mbaVersion: string;
  }, isTwoFaLogin: boolean): void => {
    let url = encodeURI(LOGIN_URL);
    if (isTwoFaLogin) {
      url = encodeURI(TWO_FA_LOGIN_URL);
      delete data.password;
    }

    const headers = TracingService.generateHeaders();

    ApiService.post(url, { data, headers })
      .then((response) => {
        if (response.data.status === REQUEST_ERROR_STATUS.TWO_FA_REQUIRED) {
          ModalService.showModal(ModalService.MODAL_KEYS.LOGIN_V2_2FA_MODAL, {
            payload: { username: data.username, ...response.data },
          });
          this.messagingService.emit(eventNames.TWO_FA_REQUIRED);

          return;
        }
        this.messagingService.emit(eventNames.LOGIN_SUCCESS, response.data);
        LocalStorageService.remove('IS_LAST_USER_GUEST_BILLING');
      })
      .catch((error) => {
        if (error?.response?.status === StatusCodes.FORBIDDEN) {
          const errorData = {
            username: data.username,
            ...error.response.data,
          };
          this.messagingService.emit(eventNames.LOGIN_ERROR_403, errorData);
        } else {
          this.messagingService.emit(eventNames.LOGIN_ERROR);
        }
      });
  };

  /**
   * @method logout - Logout
   * {@link https://cam4com.atlassian.net/wiki/spaces/RBN/pages/1705607215/Login+V2+Api#Logout }
   * @emits USER_LOGGED_OUT_ERROR when API call has error
   * @returns response (as fallback to old flow that require Promise to be returned to take some action after response)
   */
  logout = async(): Promise<AxiosResponse> => {
    const isGuestBilling = userStore.user.accessLevel === ACCESS_LEVEL_NUM.GUEST_BILLING;
    if (userStore.isLogoutResponsePending) {
      return Promise.reject();
    }

    this.messagingService.emit(eventNames.USER_LOGGED_OUT_PENDING, true);

    SessionStorageService.remove(IMPERSONATE_KEY);

    const url = encodeURI(LOGOUT_URL);

    try {
      const response = await ApiService.post(
        url,
        { headers: { 'content-type': 'application/x-www-form-urlencoded' } }
      );
      this.messagingService.emit(eventNames.GET_USER_DATA_RESPONSE, response.data);
      LocalStorageService.set('IS_LAST_USER_GUEST_BILLING', isGuestBilling.toString());

      return response;
    } catch (error) {
      parseCatch(error);
      this.messagingService.emit(eventNames.USER_LOGGED_OUT_ERROR, error.data);

      throw new Error();
    }
  };

  /**
   * @method verifyTwoFaCode - Verifying 2FA code
   * {@link https://cam4com.atlassian.net/wiki/spaces/RBN/pages/2124513287/2FA+Sms+Code+APIs#Verifying-2FA-code }
   * @param params.username - username
   * @param params.operationId - received on the 2FA required response
   * @param params.code - code from input field
   * @param params.token - received on the 2FA required response
   * @emits VERIFY_2FA_CODE_RESPONSE - when API call is successful
   * @emits VERIFY_2FA_CODE_ERROR - when API call has error
   */
  verifyTwoFaCode = (params: {
    username: User['username'];
    operationId: string;
    code: string;
    token: string;
  }): void => {
    const { username, token, operationId, code } = params;
    const url = ApiService.substitute(TWO_FA_URL, { username, operationId });
    const data = { code, token };

    ApiService.put(encodeURI(url), { data })
      .then((res) => {
        this.messagingService.emit(eventNames.VERIFY_2FA_CODE_RESPONSE, res.data);
      })
      .catch((error) => {
        switch (error.response.status) {
          case StatusCodes.FORBIDDEN: {
            this.messagingService.emit(eventNames.VERIFY_2FA_CODE_ERROR_403, error.response.data);
            break;
          }
          case StatusCodes.NOT_FOUND: {
            this.messagingService.emit(eventNames.VERIFY_2FA_CODE_ERROR_404, error.response.data);
            break;
          }
          default: {
            this.messagingService.emit(eventNames.VERIFY_2FA_CODE_ERROR, error.response.data);
          }
        }
      });
  };

  /**
   * @method resendTwoFaCode - Resending 2FA code
   * {@link https://cam4com.atlassian.net/wiki/spaces/RBN/pages/2124513287/2FA+Sms+Code+APIs#Resending-2FA-code }
   * @param params.username - username
   * @param params.token - received on the 2FA required response
   * @param params.operationId - received on the 2FA required response
   * @emits RESEND_2FA_CODE_RESPONSE - when API call is successful
   * @emits RESEND_2FA_CODE_ERROR - when API call has error
   */
  resendTwoFaCode = (params: {
    username: User['username'];
    token: string;
    operationId: string;
  }): void => {
    const { username, token, operationId } = params;
    const url = ApiService.substitute(TWO_FA_URL, { username, operationId });
    const data = { token };

    ApiService.patch(encodeURI(url), { data })
      .then(() => {
        this.messagingService.emit(eventNames.RESEND_2FA_CODE_RESPONSE);
      })
      .catch((error) => {
        switch (error.response.status) {
          case StatusCodes.FORBIDDEN: {
            this.messagingService.emit(eventNames.RESEND_2FA_CODE_ERROR_403, error.response.data);
            break;
          }
          default: {
            this.messagingService.emit(eventNames.RESEND_2FA_CODE_ERROR, error.response.data);
          }
        }
      });
  };

  /**
   * @method addNewUserModal - Add new user modal
   * {@link https://cam4com.atlassian.net/wiki/spaces/RBN/pages/1452146761/User+Modals }
   * @param modalData.username - username
   * @param modalData.modalType -name of the modal
   * @param modalData.action - action of the modal
   */
  addNewUserModal = (modalData: {
    username: User['username'];
    modalType: string;
    action: string;
  }): void => {
    const { username, modalType, action } = modalData;
    const url = ApiService.substitute(USER_MODALS_URL, { username });
    const data = { modalType, action };
    ApiService.post(encodeURI(url), { data });
  };

  getUiVariables = (): void => {
    const url = encodeURI(UI_VARIABLES_URL);
    ApiService.get(url).then((res) => {
      const uiVersionChanged = res.data.uiVersion !== ConfigurationService.get(ConfigurationKeys.UI_VERSION);
      this.messagingService.emit(eventNames.TOGGLE_VERSION_UPDATE, uiVersionChanged);
    });
  };
}

export default new UserService(getMessagingServiceInstance());
